import { useMemo, useState } from "react";
import { AudienceVisitorsTableMapContextType } from "../types";
import { groupBy, random } from "lodash";
import { Audience } from "@/migrated-widgets/table-map/services/MapDataLayer/types/Engage.types";
import { audienceRecordUniqueId } from "../constants";

export const useAudienceVisitorsMarkers = (audiences: Audience[]) => {
  // ? TagMakers filters
  const [zipCodes, setZipCodes] = useState<string[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<string[]>([]);
  const [audiencesIds, setAudiencesIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [makeIds, setMakeIds] = useState<string[]>([]);
  const [modelIds, setModelIds] = useState<string[]>([]);
  const [resetZoom, setResetZoom] = useState(true);

  const granularAudiences = useMemo(() => {
    const _audiences = audiences
      .flatMap((audience) => audience.dataCollection)
      .filter((audience) => audience.data.audienceId && audience.data.groupId);
    return _audiences;
  }, [audiences]);

  const audienceMarkers = useMemo((): AudienceVisitorsTableMapContextType["audienceMarkers"] => {
      let filteredAudiences = granularAudiences;
      if (zipCodes.length > 0) filteredAudiences = filteredAudiences.filter((audience) => zipCodes.includes(audience.data.audienceVisitorReportZipCode));
      else if (groupIds.length > 0) filteredAudiences = filteredAudiences.filter((audience) => groupIds.includes(audience.data.groupId));
      else if (audiencesIds.length) filteredAudiences = filteredAudiences.filter((audience) => audiencesIds.includes(audienceRecordUniqueId(audience)));
      else if (makeIds.length > 0) {
          filteredAudiences = filteredAudiences.filter((audience) => makeIds.includes(`${audience.data.groupId}_${audience.data.audienceVisitorReportMakeId}`));
      } else if (modelIds.length > 0) {
          filteredAudiences = filteredAudiences.filter((audience) =>
              modelIds.includes(`${audience.data.groupId}_${audience.data.audienceVisitorReportMakeId}_${audience.data.audienceVisitorReportModelId}`),
          );
      } else if (vehicleTypes.length > 0) {
          filteredAudiences = filteredAudiences.filter((audience) =>
              vehicleTypes.includes("NEW") ? Boolean(audience.data.audienceVisitorReportModelId) : true,
          );
      }

      const audiencesGroupedByZipCode = groupBy(filteredAudiences, (audience) => audience.data.audienceVisitorReportZipCode);

      return Object.keys(audiencesGroupedByZipCode).map((zipCode) => {
          const _audiences = audiencesGroupedByZipCode[zipCode];
          const [firstAudience] = _audiences;
          const visitorsCount = _audiences.reduce((acc: number, audience) => acc + audience.data.visitorsCount, 0);

          return {
              id: `${firstAudience.data.audienceVisitorReportZipCode}-${random(false)}`,
              position: {
                  lat: firstAudience.data.audienceVisitorReportLatitude,
                  lng: firstAudience.data.audienceVisitorReportLongitude,
              },
              visitorsCount: visitorsCount,
              zipCode: firstAudience.data.audienceVisitorReportZipCode,
          };
      });
  }, [granularAudiences, zipCodes, groupIds, audiencesIds, makeIds, modelIds, vehicleTypes]);

  return {
    zipCodes,
    setZipCodes,
    audienceMarkers,
    audiencesIds,
    setAudiencesIds,
    vehicleTypes,
    setVehicleTypes,
    groupIds,
    makeIds,
    modelIds,
    resetZoom,
    setGroupIds,
    setMakeIds,
    setModelIds,
    setResetZoom
  };
};
